/**
 * useUnsavedChangesWarning Hook
 * Warns user when navigating away with unsaved changes
 * Uses browser beforeunload event to prevent data loss
 */

import { useEffect } from 'react';

interface UseUnsavedChangesWarningOptions {
  when: boolean;
  message?: string;
}

/**
 * Hook to show warning when user tries to navigate with unsaved changes
 *
 * Note: Uses only beforeunload event for browser compatibility.
 * Modern browsers show a generic message, not the custom message parameter.
 */
export function useUnsavedChangesWarning({
  when,
  message = 'You have unsaved changes. Do you want to leave without saving?',
}: UseUnsavedChangesWarningOptions) {
  // Handle browser navigation (refresh, close tab, back button)
  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Prevent navigation
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = message;
      // Some browsers use the return value
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, message]);

  return {
    isBlocked: false,
  };
}
